import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';

import { ReactComponent as ArrowIcon } from '@repo/common/assets/icons/24px/icon_24px_arrow-left.svg';

import ApplyInfo from './ApplyInfo';
import Resume from './Resume';
import AdditionalUpload from './AdditionalUpload';
import BottomButton from './BottomButton';
import BottomText from './BottomText';
import ChangeResumeModalContent from './ChangeResumeModalContent';
import JobCompanyLogo from '../JobCompanyLogo';
import { BaseButton, CloseButton, useIsMobile, useT } from '@repo/common';
import ResumeBottomSheet from '../../MyPage/MyApply/Applications/ResumeBottomSheet';

const ApplyNowModal = ({ job, isOpen, onClose, onSuccess }) => {
  const t = useT();
  const isMobile = useIsMobile();
  const [view, setView] = useState('apply');
  const [applyInfo, setApplyInfo] = useState({
    firstName: '',
    lastName: '',
    phoneCountry: '',
    phone: '',
    email: '',
    visaType: '',
  });
  const [uploadData, setUploadData] = useState({
    files: [],
    links: [],
  });

  const applyRef = useRef(null);
  const changeRef = useRef(null);

  const [selectedResume, setSelectedResume] = useState(null);
  const [submitAttemptedInfo, setSubmitAttemptedInfo] = useState(false);
  const [submitAttemptedResume, setSubmitAttemptedResume] = useState(false);
  const [submitAttemptedUpload, setSubmitAttemptedUpload] = useState(false);
  const [uploadInvalid, setUploadInvalid] = useState(false);
  const [infoInvalid, setInfoInvalid] = useState(false);
  const [applyScrollOverflow, setApplyScrollOverflow] =
    useState('overflow-y-auto');
  const [changeScrollOverflow, setChangeScrollOverflow] =
    useState('overflow-y-auto');

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  useEffect(() => {
    if (view === 'apply') {
      setChangeScrollOverflow('overflow-hidden');
      setTimeout(() => setApplyScrollOverflow('overflow-y-auto'), 100);
    }
    if (view === 'changeResume') {
      setApplyScrollOverflow('overflow-hidden');
      setTimeout(() => setChangeScrollOverflow('overflow-y-auto'), 100);
    }
  }, [view]);

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <div
      className='fixed inset-0 z-[1000000] flex items-stretch justify-center bg-surface-black60 p-0 tabletH:items-center tabletH:p-6 tabletH:pb-[40px]'
      onClick={onClose}
    >
      <div
        className='flex h-full w-full flex-col items-center bg-grayscale-gray01 rounded-none tabletH:h-auto tabletH:max-h-full tabletH:w-[760px] tabletH:max-w-[760px] tabletH:justify-center tabletH:rounded-[30px]'
        onClick={(e) => e.stopPropagation()}
      >
        <div className='w-full shrink-0 flex flex-col'>
          <div className='flex items-center justify-between px-4 pt-4 pb-3 tabletH:px-[30px] tabletH:pt-6 tabletH:pb-[18px]'>
            <div className='flex h-6 w-6 items-center'>
              {isMobile ? (
                <CloseButton type='default' onClick={onClose} />
              ) : (
                view === 'changeResume' && (
                  <BaseButton
                    type='text'
                    size='s'
                    onClick={() => setView('apply')}
                    className='h-auto min-w-0 rounded-none border-0 bg-transparent p-0 hover:bg-transparent'
                    icon={<ArrowIcon />}
                  />
                )
              )}
            </div>

            <div className='typo-b16Strong text-text-primary'>
              {view === 'changeResume'
                ? t('Change resume', '이력서 변경')
                : t('Apply now', '지원하기')}
            </div>

            <div className='flex h-6 w-6 items-center justify-end'>
              {!isMobile && <CloseButton type='default' onClick={onClose} />}
            </div>
          </div>
          <div className='border-b border-grayscale-gray02' />
        </div>

        <div className='relative w-full flex-1 min-h-0 overflow-hidden tabletH:basis-[642px] tabletH:grow-0 tabletH:shrink'>
          <div
            className={`
              flex w-[200%] h-full transition-transform duration-300 ease-in-out
              ${!isMobile && view === 'changeResume' ? '-translate-x-1/2' : 'translate-x-0'}
            `}
          >
            <div
              className={`w-1/2 pt-[30px] pb-[160px] px-4 tabletH:pt-0 tabletH:px-[30px] tabletH:pb-6 flex flex-col gap-10 tabletH:gap-0 ${applyScrollOverflow}`}
              ref={applyRef}
            >
              {/* 지원 대상 공고 요약 */}
              <div className='flex w-full shrink-0 items-start gap-4 tabletH:hidden'>
                <JobCompanyLogo
                  src={job?.companyLogoUrl}
                  sizeClassName='size-[46px] shrink-0'
                  textClassName='typo-b12'
                  backgroundClassName='bg-grayscale-gray00'
                />
                <div className='flex min-w-0 flex-col justify-center gap-1'>
                  <div className='flex min-w-0 items-center gap-2'>
                    <span className='typo-b12Strong truncate text-text-primary'>
                      {job?.companyName || job?.company}
                    </span>
                    <span className='size-[3px] shrink-0 rounded bg-icon-placeholder' />
                    <span className='typo-b12 truncate text-text-placeholder'>
                      {job?.location}
                    </span>
                  </div>
                  <p className='typo-b16Strong text-text-primary'>
                    {job?.title}
                  </p>
                </div>
              </div>

              <ApplyInfo
                status={'edit'}
                submitAttempted={submitAttemptedInfo}
                onValidityChange={(invalid) => {
                  setInfoInvalid(invalid);

                  if (!invalid) {
                    setSubmitAttemptedInfo(false);
                  }
                }}
                onChange={setApplyInfo}
                $isMobile={isMobile}
              />
              <Resume
                status={'edit'}
                selectedResume={selectedResume}
                submitAttempted={submitAttemptedResume}
                onChangeResume={() => setView('changeResume')}
              />
              <AdditionalUpload
                status={'edit'}
                job={job}
                files={uploadData.files}
                links={uploadData.links}
                setUploadData={setUploadData}
                submitAttempted={submitAttemptedUpload}
                onValidityChange={(invalid) => {
                  const hasValidFile = uploadData.files.some(
                    (item) => !item.error,
                  );
                  setUploadInvalid(!hasValidFile);
                }}
                $isMobile={isMobile}
              />
              <BottomText />
            </div>

            {!isMobile && (
              <div
                className={`w-1/2 px-[30px] py-6 flex flex-col ${changeScrollOverflow}`}
                ref={changeRef}
              >
                <ChangeResumeModalContent
                  selectedResume={selectedResume}
                  onSelectResume={setSelectedResume}
                  onApply={() => setView('apply')}
                />
              </div>
            )}
          </div>
        </div>

        <BottomButton
          view={view}
          status='edit'
          onClose={onClose}
          onChangeResumeConfirm={() => setView('apply')}
          applyInfo={applyInfo}
          resumeData={selectedResume}
          infoInvalid={infoInvalid}
          uploadData={uploadData}
          onSubmitAttempt={({ infoInvalid, resumeInvalid }) => {
            setSubmitAttemptedInfo(false);
            setSubmitAttemptedResume(false);
            setSubmitAttemptedUpload(false);

            if (infoInvalid) setSubmitAttemptedInfo(true);
            if (resumeInvalid) setSubmitAttemptedResume(true);
            if (uploadInvalid) setSubmitAttemptedUpload(true);
          }}
          onSuccess={onSuccess}
          jobId={job?.id}
          job={job}
        />

        {isMobile && (
          <ResumeBottomSheet
            title={t('Change resume', '이력서 변경')}
            isOpen={view === 'changeResume'}
            onClose={() => setView('apply')}
            footer={
              <BottomButton
                view={view}
                status='edit'
                onClose={onClose}
                onChangeResumeConfirm={() => setView('apply')}
                applyInfo={applyInfo}
                resumeData={selectedResume}
                infoInvalid={infoInvalid}
                uploadData={uploadData}
                onSubmitAttempt={({ infoInvalid, resumeInvalid }) => {
                  setSubmitAttemptedInfo(false);
                  setSubmitAttemptedResume(false);
                  setSubmitAttemptedUpload(false);

                  if (infoInvalid) setSubmitAttemptedInfo(true);
                  if (resumeInvalid) setSubmitAttemptedResume(true);
                  if (uploadInvalid) setSubmitAttemptedUpload(true);
                }}
                onSuccess={onSuccess}
                jobId={job?.id}
                job={job}
                $isMobile={isMobile}
              />
            }
          >
            <ChangeResumeModalContent
              selectedResume={selectedResume}
              onSelectResume={setSelectedResume}
              onApply={() => setView('apply')}
            />
          </ResumeBottomSheet>
        )}
      </div>
    </div>,
    document.body,
  );
};

export default ApplyNowModal;
